<template>
  <VModal
    :showing="showing"
    container-classes="max-w-lg w-full"
    bordered-footer="borderedFooter"
    overflow-content="overflowContent"
    @update:showing="$emit('update:showing', $event)"
  >
    <template #header>
      <hgroup>
        <h2 class="font-normal leading-6 m-0 text-lg">Create Meeting Booking Slide</h2>
        <h3 class="font-normal text-sm text-gray-600 mb-0">Allow investors to book a meeting with you from a slide</h3>
      </hgroup>
    </template>

    <template #default>
      <form class="flex flex-col gap-8 mb-6" data-testid="create-meeting-slide" @submit.prevent="createMeetingSlide">
        <FormField
          v-model="formTitle"
          :attrs="titleAttrs"
          label="Slide Title"
          required="required"
          placeholder="eg: Book a meeting with us"
          :error="errors.title"
          :maxlength="60"
          data-testid="slide-title"
        />

        <VDropdown
          v-model="currentUser"
          class="w-full"
          size="sm"
          :options="settingUsers"
          data-testid="create-meeting-slide-dropdown"
          @update:model-value="setCurrentUser"
        >
          <template #current-single="{ item }">{{ item.name }}</template>
          <template #icon>
            <span class="text-gray-400"><ChevronUpDownIcon class="w-5" /></span>
          </template>
          <template #option="{ item, selected }">
            <VDropdownItem
              :item="item.name"
              :selected="selected"
              :class="{ 'text-gray-900 font-semibold': selected }"
              :data-testid="`dropdown-item-${item.id}`"
            />
          </template>
        </VDropdown>

        <VMessage
          v-if="requestSettings.status === 'incomplete'"
          class="items-center"
          :show="true"
          banner-type="warning"
          theme="mobile"
          data-testid="create-meeting-slide-incomplete"
          >Looks like you haven't set up your meeting booking tool. To use your availability, please set it up
          <template #dismiss>
            <VButton
              class="w-full inline-block text-center"
              component="a"
              :href="`/meetings/book/setup/intro?companyId=${deck.company_id}`"
              target="_blank"
              size="sm"
              no-shadow="noShadow"
              no-border="noBorder"
              @click="void 0"
              >Set up this feature
            </VButton>
          </template>
        </VMessage>

        <VMessage
          v-if="requestSettings.status === 'inactive'"
          class="items-center"
          :show="true"
          banner-type="warning"
          theme="mobile"
          data-testid="create-meeting-slide-inactive"
          >Looks like your meeting booking tool is turned off. Please turn it on to use your availability
          <template #dismiss>
            <VButton class="w-full" size="sm" no-shadow="noShadow" no-border="noBorder" @click="$emit('activate-book-a-meeting')">
              Turn on
            </VButton>
          </template>
        </VMessage>
      </form>
    </template>

    <template #footer>
      <div class="flex justify-end gap-3">
        <VButton data-testid="cancel-btn" size="md" @click="$emit('update:showing', false)">Cancel</VButton>
        <VButton variant="copper" data-testid="create-btn" size="md" type="submit" @click="createMeetingSlide">Create</VButton>
      </div>
    </template>
  </VModal>
</template>

<script lang="ts" setup>
import type { DeepReadonly } from 'vue'
import { z } from 'zod'
import { useForm } from 'vee-validate'
import ZodMessages from '@/zod_presets/messages'
import { toTypedSchema } from '@vee-validate/zod'

import type { Option } from '@/components/VDropdown.vue'
import type { EditDeck } from '@/composables/useEditDeck'
import type { BookMeetingStatus } from '@/composables/useBookMeeting'
import type { ScheduleSetting } from '@/entities/meeting'
import { ChevronUpDownIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  showing: {
    type: Boolean,
    required: true,
  },
  requestSettings: {
    type: Object as PropType<DeepReadonly<BookMeetingStatus>>,
    required: true,
  },
  deck: {
    type: Object as PropType<DeepReadonly<EditDeck>>,
    required: true,
  },
})

const emit = defineEmits(['update:showing', 'add-meeting-slide', 'activate-book-a-meeting'])

const title = ref('')
const { requestSettings, deck } = toRefs(props)
const { currentUser: reqSettingsUser, users } = useBookMeetingDropdown(requestSettings)

const Schema = z.object({
  title: z.string().nonempty(ZodMessages.title).max(60).default(''),
})

const { handleSubmit, defineField, errors } = useForm<z.infer<typeof Schema>>({
  validationSchema: toTypedSchema(Schema),
})

const [formTitle, titleAttrs] = defineField('title')

const currentUser = ref({ name: reqSettingsUser.value?.user.name, id: reqSettingsUser.value?.id } as Option)
const settingUsers = computed(() => users.value.map((u: any, index: number) => ({ name: u.user.name, id: u.id })))

function setCurrentUser(user: Option) {
  currentUser.value = user
}

const createMeetingSlide = handleSubmit(async () => {
  emit('add-meeting-slide', {
    type: 'E',
    deck_id: deck.value.id,
    title: formTitle.value,
    request_setting_id: currentUser.value.id,
  })
  emit('update:showing', false)

  formTitle.value = ''
  reqSettingsUser.value = users.value[0] as ScheduleSetting
})
</script>
